import axios from 'axios';
import { ACCOUNT_SELECTION_KEY, getPendingAccountSelection } from './accountSelection';
import { v4 as uuidv4 } from 'uuid';
import {
  clearAuthRedirectInProgress,
  shouldSuppressSessionExpiredFeedback,
  showToast,
} from '../Utils/common';

const requiresCurrentAccount = (config) =>
  Boolean(sessionStorage.getItem('accessToken')) &&
  !config.skipAuthHeader &&
  !/\/Account\/(?:login|logout|UserDetails|UserDetailsSSO|validuser)(?:[/?]|$)/i.test(config.url || '');

// Use a module pattern to avoid exporting mutable let
const apiModule = {
  instance: null,
 
  initialize: function(apiUrl) {
    this.instance = axios.create({
      baseURL: apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor
    this.instance.interceptors.request.use(config => {
      if (requiresCurrentAccount(config) && getPendingAccountSelection()) {
        window.dispatchEvent(new Event("accountSelectionRequired"));
        throw new axios.CanceledError("Account changed in another tab. Confirm the account-change warning before continuing.");
      }
      config.accountSelectionSnapshot = localStorage.getItem(ACCOUNT_SELECTION_KEY);
  config.headers['X-CORRELATION-ID'] = uuidv4();

      const skipAuthHeader = config?.skipAuthHeader === true;
  const token = sessionStorage.getItem('accessToken');

  if (token && !skipAuthHeader) {
        config.headers['Authorization'] = `Bearer ${token}`;
      } else if (skipAuthHeader) {
        delete config.headers['Authorization'];
      }

      try {
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          if (config.headers['Content-Type']) delete config.headers['Content-Type'];
          if (config.headers['content-type']) delete config.headers['content-type'];
        } else {
          if (!config.headers['Content-Type'] && !config.headers['content-type']) {
            config.headers['Content-Type'] = 'application/json';
          }
        }
      } catch (err) {
      }

      return config;
    }, error => {
      return Promise.reject(error);
    });

    // Setup methods with autoSafe wrapper
    const autoSafe = (fn) => async (...args) => this._handleRequest(fn(...args));
   
    this.instance.get = autoSafe(this.instance.get.bind(this.instance));
    this.instance.post = autoSafe(this.instance.post.bind(this.instance));
    this.instance.put = autoSafe(this.instance.put.bind(this.instance));
    this.instance.delete = autoSafe(this.instance.delete.bind(this.instance));
    this.instance.patch = autoSafe(this.instance.patch.bind(this.instance));
    this.instance.request = autoSafe(this.instance.request.bind(this.instance));

    this.instance.interceptors.response.use(
      response => {
        if (
          requiresCurrentAccount(response.config) &&
          (
            getPendingAccountSelection() ||
            response.config.accountSelectionSnapshot !== localStorage.getItem(ACCOUNT_SELECTION_KEY)
          )
        ) {
          window.dispatchEvent(new Event("accountSelectionRequired"));
          throw new axios.CanceledError("Account changed in another tab. Discarding the stale response.");
        }
        return response;
      },
      error => Promise.reject(error)
    );
  },

  // Helper: Check if network error with unresolved hostname
  _isNetworkNameError: function(error) {
    return error.code === "ERR_NETWORK" && error.message.includes("ERR_NAME_NOT_RESOLVED");
  },

  // Helper: Handle network/DNS errors
  _handleNetworkError: function() {
    localStorage.clear();
     sessionStorage.clear();
    sessionStorage.removeItem('accessToken');
    showToast("Unable to reach the server.", "warning");
    globalThis.location.href = "/login";
  },

  // Helper: Handle 400 Bad Request
  _handleBadRequest: function(error) {
    const hasCustomErrorMessages = error?.response?.data?.errorMessages?.length > 0;
    if (!hasCustomErrorMessages) {
      showToast('Invalid input.', 'danger');
    }
  },

  // Helper: Handle 401 Unauthorized
  _handleUnauthorized: function() {
    const suppressSessionFeedback = shouldSuppressSessionExpiredFeedback();

    localStorage.clear();
    sessionStorage.removeItem('accessToken');
    clearAuthRedirectInProgress();

    if (!suppressSessionFeedback) {
      showToast('Your session has expired. Please log in again.', 'warning');
    }

    if (globalThis.location.pathname !== "/login" && globalThis.location.pathname !== "/") {
      globalThis.location.href = "/login";
    }
  },

  // Helper: Handle server errors (500, 502, 503, 504)
  _handleServerError: function(status, error) {
    if ([500, 502, 503, 504].includes(status)) {
      showToast(
        "Please contact your administrator for assistance. " +
        error.response.data.message,
        'danger'
      );
    }
  },

  // Helper: Route error to appropriate handler
  _routeErrorHandler: function(error, status) {
    if (this._isNetworkNameError(error)) {
      this._handleNetworkError();
      return;
    }
    if (status === 400) {
      this._handleBadRequest(error);
      return;
    }
    if (status === 401) {
      this._handleUnauthorized();
      return;
    }
    this._handleServerError(status, error);
  },

  _handleRequest: async function(promise) {
  try {
    return await promise;
  } catch (error) {
    const { response, config } = error;
    const status = response?.status;
    const skipToast = config?.skipAutoSafeToast;
    const skip401Handler = config?.skip401Handler;

    if (!(status === 401 && skip401Handler)) {
      if (!skipToast) {
        this._routeErrorHandler(error, status);
      }
    }

    throw error;
  }
},

  getInstance: function() {
    if (!this.instance) {
      throw new Error('API not initialized. Call initApi first.');
    }
    return this.instance;
  }
};

// Export initialization function and getter instead of mutable let
export function initApi(apiUrl) {
  apiModule.initialize(apiUrl);
}

export const api = new Proxy({}, {
  get: (target, prop) => {
    return apiModule.getInstance()[prop];
  }
});
